import { generateId } from "../../utils/generateId";
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { RootState } from "../rootReducer";
import { RecentView } from "../../models/recent-view";
import { makeSyncUpsert } from "../syncUpsert";

export const recentViewsAdapter = createEntityAdapter<RecentView>();
type RecentViewsState = EntityState<RecentView, string>;
const initialState: RecentViewsState = recentViewsAdapter.getInitialState();

type RecentViewPayload = {
  recipeId: string;
};

export const recentViewsSlice = createSlice({
  name: "recentViews",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(recentViewsAdapter),
    addRecentView(
      state: RecentViewsState,
      action: PayloadAction<RecentViewPayload>,
    ): void {
      // Check if this recipe already has a recent view entry
      const existing = Object.values(state.entities).find(
        (rv) => rv?.recipeId === action.payload.recipeId,
      );

      const now = new Date().toISOString();

      if (existing) {
        // Update the timestamp; clear any tombstone so a re-view resurrects it
        recentViewsAdapter.updateOne(state, {
          id: existing.id,
          changes: { viewedAt: now, deletedAt: undefined },
        });
      } else {
        // First time viewing — create new entry
        recentViewsAdapter.addOne(state, {
          id: generateId(),
          recipeId: action.payload.recipeId,
          viewedAt: now,
          createdAt: now,
          updatedAt: now,
        });
      }
    },
    clearRecentlyViewed(state: RecentViewsState): void {
      // Soft-delete so the clear propagates via sync
      const now = new Date().toISOString();
      const updates = Object.values(state.entities)
        .filter((rv) => rv && !rv.deletedAt)
        .map((rv) => ({ id: rv!.id, changes: { deletedAt: now } }));
      recentViewsAdapter.updateMany(state, updates);
    },
  },
});

// selectAll* hides soft-deleted rows; sync reads entities directly.
export const selectAllRecentlyViewed = createSelector(
  recentViewsAdapter.getSelectors((state: RootState) => state.recentViews).selectAll,
  (recentViews) => recentViews.filter((rv) => !rv.deletedAt),
);

export const {
  addRecentView,
  clearRecentlyViewed,
  upsertManyFromSync: upsertRecentViewsFromSync,
} = recentViewsSlice.actions;

export default recentViewsSlice.reducer;
